import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/base/Button';
import Loading from '@/components/base/Loading';
import ErrorState from '@/components/base/ErrorState';
import EmptyState from '@/components/base/EmptyState';
import ProjectCard from './components/ProjectCard';
import ProjectToolbar from './components/ProjectToolbar';
import { useProjects } from '@/hooks/useProjects';
import { useFinancialMovements } from '@/hooks/useFinancialMovements';
import { useProjectReceiptDates } from '@/hooks/useReceipts';
import { getProjectAlerts } from '@/lib/alerts';
import { STATUS_FILTERS, type StatusFilter } from '@/lib/projectStatus';
import { formatCurrency } from '@/lib/format';

interface StatCardProps {
  icon: string;
  label: string;
  value: number | string;
  accent: string;
  valueClass: string;
}

function StatCard({ icon, label, value, accent, valueClass }: StatCardProps) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3.5 md:p-4">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${accent}`}>
        <i className={`${icon} text-xl`}></i>
      </span>
      <div className="min-w-0">
        <p className={`font-heading text-xl font-extrabold leading-none ${valueClass}`}>{value}</p>
        <p className="mt-1 truncate text-xs text-foreground-500">{label}</p>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { projects, loading, error, reload } = useProjects();
  const { movements } = useFinancialMovements();
  const { receiptDates } = useProjectReceiptDates();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('TODOS');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return projects.filter((project) => {
      const matchStatus = status === 'TODOS' || project.status === status;
      const matchTerm =
        !term ||
        project.name.toLowerCase().includes(term) ||
        (project.clients?.name || '').toLowerCase().includes(term);
      return matchStatus && matchTerm;
    });
  }, [projects, search, status]);

  const stats = useMemo(() => {
    return {
      active: projects.filter((project) => project.status === 'ACTIVO').length,
      progress: projects.filter((project) => project.status === 'EN PROCESO').length,
      billingUpcoming: projects.filter((project) => {
        const state = getProjectAlerts(project, new Date(), receiptDates[project.id]).billingState;
        return state === 'pendiente' || state === 'hoy';
      }).length,
      contractEnding: projects.filter((project) => {
        const state = getProjectAlerts(project).contractState;
        return state === 'por_finalizar' || state === 'finalizado';
      }).length,
    };
  }, [projects, receiptDates]);

  const finance = useMemo(() => {
    const income = movements
      .filter((movement) => movement.movement_type === 'INGRESO')
      .reduce((sum, movement) => sum + Number(movement.amount), 0);
    const expense = movements
      .filter((movement) => movement.movement_type === 'EGRESO')
      .reduce((sum, movement) => sum + Number(movement.amount), 0);
    return { income, balance: income - expense };
  }, [movements]);

  return (
    <div className="flex flex-col gap-6">
      <section className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatCard
          icon="ri-checkbox-circle-line"
          label="Proyectos activos"
          value={stats.active}
          accent="bg-primary-100 text-primary-700"
          valueClass="text-primary-700"
        />
        <StatCard
          icon="ri-time-line"
          label="En proceso"
          value={stats.progress}
          accent="bg-accent-100 text-accent-700"
          valueClass="text-accent-700"
        />
        <StatCard
          icon="ri-alarm-warning-line"
          label="Cobros próximos"
          value={stats.billingUpcoming}
          accent="bg-amber-100 text-amber-700"
          valueClass="text-amber-700"
        />
        <StatCard
          icon="ri-file-shield-2-line"
          label="Contratos por finalizar"
          value={stats.contractEnding}
          accent="bg-red-100 text-red-600"
          valueClass="text-red-600"
        />
        <StatCard
          icon="ri-arrow-down-circle-line"
          label="Ingresos"
          value={formatCurrency(finance.income)}
          accent="bg-secondary-100 text-secondary-700"
          valueClass="text-foreground-950"
        />
        <StatCard
          icon="ri-wallet-3-line"
          label="Saldo actual"
          value={formatCurrency(finance.balance)}
          accent="bg-background-100 text-foreground-600"
          valueClass={finance.balance < 0 ? 'text-red-600' : 'text-foreground-950'}
        />
      </section>

      <section className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950">Proyectos</h2>
            <p className="text-sm text-foreground-500">
              {filtered.length} de {projects.length} proyectos mostrados
            </p>
          </div>
          <Button icon="ri-add-line" onClick={() => navigate('/proyectos/nuevo')}>
            Nuevo proyecto
          </Button>
        </div>

        <ProjectToolbar
          search={search}
          onSearchChange={setSearch}
          status={status}
          onStatusChange={setStatus}
        />

        {loading ? (
          <div className="rounded-lg border border-background-200 bg-background-50">
            <Loading label="Cargando proyectos..." />
          </div>
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : projects.length === 0 ? (
          <EmptyState
            icon="ri-folders-line"
            title="Todavía no hay proyectos"
            description="Registre su primer proyecto para comenzar a realizar el seguimiento de cobros y contratos."
            action={
              <Button icon="ri-add-line" onClick={() => navigate('/proyectos/nuevo')}>
                Nuevo proyecto
              </Button>
            }
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-search-eye-line"
            title="Sin resultados"
            description="No se encontraron proyectos con los filtros aplicados. Pruebe con otro criterio de búsqueda."
            action={
              <Button
                variant="outline"
                icon="ri-refresh-line"
                onClick={() => {
                  setSearch('');
                  setStatus(STATUS_FILTERS[0].value);
                }}
              >
                Limpiar filtros
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                index={index}
                receiptDates={receiptDates[project.id]}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}